export interface PayerAuthSetup {
  /** card_accountNumber */
  cardNumberHidden: string;
  /** card_expirationMonth */
  expirationMonth: string;
  /** card_expirationYear */
  expirationYear: string;
  /** merchantReferenceCode */
  invoiceNumber: string;
  orderId: string;
  cardholderName: string;
  orderDetails: ConsumerOrderDetails;
}

export interface PayerAuthSetupResponse {
  decision: string;
  merchantReferenceCode: string;
  deviceDataCollectionURL: string;
  reasonCode: string;
  referenceId: string;
  accessToken: string;
  requestId: string;
  requestToken: string;
  rejectReasonDescription: string;
}

export interface PaymentAuthorizationResponse {
  Approved: boolean;
  ReasonCode: string;
  Transaction: {
    PaymentID: any;
    OrderID?: any;
    CustomerID?: any;
    PaymentNumber: number;
    OrderNumber?: number;
    CustID?: number;
    SessionID: string;
    GatewayID: string;
    SiteID: number;
    TransactionType: string;
    TransactionCode: string;
    Amount: number;
    Currency: string;
    TransactionTag: string;
    AuthorizationCode: string;
    TransactionRef: string;
    TransactionDate: Date;
    CreditCardType: string;
    CardBin: string;
    CreditCardLast4: string;
    CreditCardOwner: string;
    ExpiryDate: string;
    ApprovalResponse: string;
    AVSResult: string;
    CVDResult: string;
    AuthReq: string;
    AuthResult: string;
    EcommFlag: string;
    CAVVResponse: string;
    ClientIP: string;
    Approved: boolean;
    TransactionClosed?: boolean;
    PaymentComments: string;
    SalesUserID: string;
    CallBankMatched?: boolean;
    CallBankNotes: string;
    SecurityHistory: string;
    ClientDeviceInfo: string;
    NavPaymentID?: number;
    SyncDate?: Date;
    WebPaymentOrderID?: any;
    WebPaymentOrderNumber?: number;
    SalesOrderID: string;
    NavCustomerID: string;
    RefPaymentID?: number;
    CartPaymentRisk: {
      PaymentID: any;
      ScoreSummary: string;
      RiskFactorResult: string;
      MinFraudResult: string;
      MinFraudInsightsResult: string;
      FraudLabsResult: string;
      SignifydResult: string;
      DateUpdated?: Date;
      CartPayments: any;
    };
    CartOrders: {
      OrderID: any;
      OrderNumber: number;
      CustomerID: any;
      CustID: number;
      SiteID: number;
      StoreID?: number;
      NavSalesOrderID: string;
      NavInvoiceID: string;
      NavShipmentID: string;
      FirstName: string;
      LastName: string;
      OrderDate: Date;
      DateStatusChanged?: Date;
      Status: string;
      WarehouseStatus: string;
      WarehouseDate?: Date;
      InvoicedDate?: Date;
      ShippingStatus: string;
      ShippingType: string;
      ShippingDate?: Date;
      ShipDate?: Date;
      PaymentType: string;
      PaymentDetails: string;
      PrimaryPaymentID?: number;
      BillingAddressID?: any;
      BillingName: string;
      BillingAddress: string;
      BillingAddress2: string;
      BillingCity: string;
      BillingProv: string;
      BillingCountry: string;
      BillingPostalCode: string;
      BillingPhone: string;
      BillingEmail: string;
      ShippingAddressID?: any;
      ShippingName: string;
      ShippingAddress: string;
      ShippingAddress2: string;
      ShippingCity: string;
      ShippingProv: string;
      ShippingCountry: string;
      ShippingPostalCode: string;
      ShippingPhone: string;
      ShippingEmail: string;
      ShippingMethod: string;
      ShipTrackingno: string;
      CurrencyCode: string;
      CurrencyRate?: number;
      ShipDeliveryOffice: string;
      PickupInStore: string;
      PromoCode: string;
      DiscountName: string;
      GiftCard: string;
      SubTotal?: number;
      Discount?: number;
      SalesTax?: number;
      ShippingCharges?: number;
      PSTTax?: number;
      GrandTotal?: number;
      RefundedAmount?: number;
      CustomerComments: string;
      SessionID: string;
      AffiliateID?: number;
      Notes: string;
      NotesWarehouse?: boolean;
      NotesForWarehouse?: boolean;
      Salesperson: string;
      FraudScore: string;
      EmailUpdateSent?: boolean;
      EmailUpdateDate?: Date;
      PSTExempt: string;
      LastEditor: string;
      LastEditDate?: Date;
      AcceptedSOPA: string;
      AcceptedDropshipTerms: boolean;
      CustomerIP: string;
      NavStatus: string;
      SyncDate?: Date;
      SyncDirection: string;
      NavPickStatus: string;
      NavShipStatus: string;
      NavPaymentStatus: string;
      HubspotDealID: string;
      PreOrder?: boolean;
      EmailPickupSent?: Date;
      EstShipmentDelivery?: Date;
      EstShipmentDeliveryUpdated?: Date;
      NavPickedPercent?: number;
      NavLocationCode: string;
      ShippingMethodCode: string;
      ShippingMethodAgent: string;
      PickupAuthorizedName: string;
      ReminderDate?: Date;
      ReminderLabel: string;
      SalesCode: string;
      Browser: string;
      NavSalesNumber: string;
      WebSalesId?: number;
      CartOrderDetails: any[];
      CartPayments: any[];
    };
  };
  Challenge: PayerAuthenticationChallenge;
  CardholderMessage: string;
}
/** Values to render a challenge window (aka Step-up iFrame) in case additional validation is required */
interface PayerAuthenticationChallenge {
  StepUpUrl: string;
  AccessToken: string;
  ChallengeWindowSize: string;
  windowDimensions: ChallengeWindowDimensions;
}

export interface ChallengeWindowDimensions {
  width: string;
  height: string;
}

export interface ValidatePayerAuth {
  /** Cybersource transaction Id */
  TransactionId: string;
  /** Url of the top window */
  Origin: string;
}

export interface PaymentAuthorizationRequest {
  TransactionMode: string;
  Account: CreditCardForRequest;
  OrderDetails: ConsumerOrderDetails;
  BrowserScreenWidth: string;
  BrowserScreenHeight: string;
  ShouldCreateNetworkToken: boolean;
  ShouldSetDefaultNetworkToken?: boolean;
  InstrumentIdentifierId: string;
}

export interface CheckEnrollAndAuthorizeRequest extends PaymentAuthorizationRequest {
  ConsumerAuthReferenceId: string;
}

export interface ValidateAuthAndAuthorizeRequest extends PaymentAuthorizationRequest {
  TransactionId: string;
}

export interface CreditCardBase {
    expirationMonth: string;
    expirationYear: string;
    cardCode: string;
    nameOnAccount: string;
    tokenId: string;
}

export interface CreditCard extends CreditCardBase {
    accountNumber: string;
}

export interface CreditCardForRequest extends CreditCardBase {
    accountNumberHidden: string;
}

export interface ConsumerOrderDetails {
  SalesOrderId: string;
  OrderNumber: any;
  Amount: number;
  CustomerComments: string;
  AcceptedTerms: boolean;
  AcceptedDropshipTerms: boolean;
  Enews: boolean;
}


export function toCreditCardForRequest(card: CreditCard): CreditCardForRequest {
    const { accountNumber, ...rest } = card;
    return {
        ...rest,
        accountNumberHidden: btoa(accountNumber),
    };
}